import { chartStart, COVERAGE, NOT_YET } from "./coverage"
import { SiteLayout, StartButtons } from "./SiteLayout"

/** A High Court's tier, drawn as dots (3, 2, 1) and said to a screen reader in vague words. The page never names a tier. */
const DOTS: Record<string, number> = { deep: 3, good: 2, thin: 1 }
const SAID: Record<string, string> = { deep: "well covered", good: "partly covered", thin: "thinly covered" }

const { first_year: FIRST, gst_start: GST } = COVERAGE

/** The widest a year's slot may be (a bar is 75% of it, about 36px), so a two-year chart is not two slabs; a capped chart is left-aligned. */
const SLOT_MAX = 48

/** One source's holdings by year, from its own start year to the last. Each bar is a year; the chart fills the card, the bars sharing its width. */
function Density({ values, start, name }: { values: number[]; start: number; name: string }) {
  const n = values.length
  const end = start + n - 1
  // The marker sits where GST began, so only a chart that starts before it and reaches it has one; every bar from GST's year on is darker.
  const gstAt = start < GST && GST <= end ? GST - start : null
  const label = `${name}, decisions held by year from ${start} to ${end}${end >= GST ? `; the bars since GST began in ${GST} are darker` : ""}`
  return (
    <div className="ncv-chart" role="img" aria-label={label} style={{ maxWidth: `${n * SLOT_MAX}px` }}>
      {gstAt !== null && (
        <span className={`ncv-gst-tag${gstAt * 2 > n ? " before" : ""}`} style={{ left: `${(gstAt / n) * 100}%` }}>GST, {GST}</span>
      )}
      <svg viewBox={`0 0 ${n} 100`} preserveAspectRatio="none" aria-hidden="true" focusable="false">
        {values.map((v, i) => {
          const h = Math.max(1.6, v * 100)
          return <rect key={i} x={i + 0.125} y={100 - h} width={0.75} height={h} className={start + i >= GST ? "gst" : undefined} />
        })}
        {gstAt !== null && <line x1={gstAt} x2={gstAt} y1={0} y2={100} className="ncv-gst" />}
      </svg>
      {/* The first year under the first bar's left edge, the last under the last bar's right edge. */}
      <div className="ncv-axis" style={{ padding: `0 ${12.5 / n}%` }}>{n > 1 ? <><span>{start}</span><span>{end}</span></> : <span>{start}</span>}</div>
    </div>
  )
}

/** Coverage (the public site): what Nira holds, court by court and year by year, and what it does not. */
export default function SiteCoverage() {
  const courts = [...COVERAGE.high_courts].sort((a, b) => (DOTS[b.tier] ?? 1) - (DOTS[a.tier] ?? 1) || a.name.localeCompare(b.name, "en"))
  return (
    <SiteLayout title="Coverage">
      <section className="nst-hero slim">
        <div className="nst-wrap nst-hero-copy">
          <p className="nst-k">Coverage · updated {new Date(COVERAGE.updated).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
          <h1>{COVERAGE.total_approx} decisions, and an honest list of what is not in yet.</h1>
          <p className="nst-lede">What Nira holds when it answers: the courts and tribunals, year by year, the law itself, and what is being added. Where it holds little, it says so in the answer.</p>
        </div>
      </section>

      <section className="nst-sec" aria-labelledby="ncv-src">
        <div className="nst-wrap nst-stack">
          <div className="nst-head-block"><p className="nst-k" id="ncv-src">Decisions, by forum</p><h2 className="nst-h2 sm">Each bar is a year. The darker ones are since GST.</h2></div>
          <div className="ncv-sources">
            {COVERAGE.sources.map((s) => {
              const start = chartStart(s)
              return (
                <article className="ncv-source" key={s.key}>
                  <div className="ncv-source-h"><h3>{s.name}</h3><span className="ncv-n">{s.approx}</span></div>
                  <p>{s.what}</p>
                  <p className="ncv-years">Held from {start} to {s.to}.</p>
                  <Density values={s.density.slice(start - FIRST)} start={start} name={s.name} />
                </article>
              )
            })}
          </div>
        </div>
      </section>

      <section className="nst-sec nst-tint" aria-labelledby="ncv-hc">
        <div className="nst-wrap nst-stack">
          <div className="nst-head-block"><p className="nst-k" id="ncv-hc">The {COVERAGE.high_courts.length} High Courts</p><h2 className="nst-h2 sm">How much of each court's work Nira holds. Your State's court is weighed first.</h2></div>
          <ul className="ncv-courts">
            {courts.map((c) => (
              <li className="ncv-court" key={c.name}>
                <span className="ncv-dots" aria-hidden="true">{[1, 2, 3].map((k) => <i key={k} className={k <= (DOTS[c.tier] ?? 1) ? "on" : undefined} />)}</span>
                <span>{c.name}</span>
                <span className="nst-sr">, {SAID[c.tier] ?? "thinly covered"}</span>
              </li>
            ))}
          </ul>
          <p className="ncv-legend">More dots: more of the court's decisions held.</p>
        </div>
      </section>

      <section className="nst-sec" aria-labelledby="ncv-law">
        <div className="nst-wrap nst-stack">
          <div className="nst-head-block"><p className="nst-k" id="ncv-law">The law itself</p><h2 className="nst-h2 sm">The Acts, the Rules, and what the Board has said.</h2></div>
          <table className="nst-gaps">
            <thead><tr><th scope="col">What</th><th scope="col">Held</th><th scope="col">Notes</th></tr></thead>
            <tbody>
              {COVERAGE.law.map((l) => (
                <tr key={l.name}><th scope="row">{l.name}</th><td className="ncv-range">{l.range}</td><td>{l.note}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="nst-sec nst-tint" aria-labelledby="ncv-coming">
        <div className="nst-wrap nst-stack">
          <div className="nst-head-block"><p className="nst-k" id="ncv-coming">What is missing, and what is being added</p><h2 className="nst-h2 sm">Said here, and in every answer it would change.</h2></div>
          <table className="nst-gaps">
            <thead><tr><th scope="col">Status</th><th scope="col">What</th><th scope="col">Where it stands</th></tr></thead>
            <tbody>
              {NOT_YET.map((x, i) => (
                <tr key={x.title} className={i > 0 && NOT_YET[i - 1].status !== x.status ? "first" : undefined}>
                  <td><span className={`nst-tag ${x.status === "In progress" ? "on" : x.status === "Next" ? "next" : "none"}`}>{x.status}</span></td>
                  <th scope="row">{x.title}</th>
                  <td>{x.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="nst-navy">
        <div className="nst-wrap nst-cta-row">
          <div className="nst-cta-copy"><h2 className="nst-h2">Ask it something it should know.</h2></div>
          <StartButtons dark />
        </div>
      </section>
    </SiteLayout>
  )
}
